/**
 * Platform config hooks — read public config values via get_platform_config RPC.
 * Used for maintenance mode, signup gates, enrollment caps, etc.
 * All hooks fail open: if the config fetch fails, the feature is treated as enabled/unrestricted.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { queryKeys } from '@/utils/queryKeys';
import { useMyCourses } from '@/hooks/useClassroom';
import { PLATFORM_CONFIG } from '@/lib/constants';

async function fetchPlatformConfig(key: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('get_platform_config', { p_key: key });
  // Fail open: if RPC errors, return null (config treated as missing)
  if (error) {
    if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
      console.warn(`[usePlatformConfig] Failed to fetch "${key}":`, error.message);
    }
    return null;
  }
  return data as string | null;
}

/**
 * Read a single platform config value.
 * Returns null if the key doesn't exist or the caller lacks access.
 * Never throws — fail open by design.
 */
export function usePlatformConfig(key: string) {
  return useQuery({
    queryKey: queryKeys.platformConfig(key),
    queryFn: () => fetchPlatformConfig(key),
    staleTime: PLATFORM_CONFIG.STALE_TIME_MS,
    // Fail open: never throw to error boundary for config reads
    throwOnError: false,
    retry: 1,
  });
}

/**
 * Convenience hook for enrollment cap awareness.
 * Returns { max, current, atCap } so the UI can show "X of Y" and disable enrollment.
 */
export function useEnrollmentCapStatus() {
  const { data: maxStr } = usePlatformConfig('max_enrollments_per_user');
  const { data: myCourses } = useMyCourses();

  const parsed = maxStr ? parseInt(maxStr, 10) : NaN;
  const max = Number.isNaN(parsed) ? null : parsed;
  const current = myCourses?.length ?? 0;
  const atCap = max !== null && current >= max;

  return { max, current, atCap };
}
